import {
  APIAttachment,
  ArdoqId,
  DocumentReference,
  DocumentResourceType,
  LoadedChat,
} from '@ardoq/api-types';
import {
  toAttachmentIcon,
  toAttachmentType,
} from '../../documentArchive/attachmentPreview';
import { MAX_DOCUMENTS_LIMIT } from '../constants';
import { isSupportedDocument, toDocumentReference } from './utils';
import type { DocumentItemViewModel, UploadingDocument } from './types';

export type {
  DocumentItemViewModel,
  UploadError,
  UploadingDocument,
} from './types';

export type OrgDocumentsState =
  | { status: 'loading' }
  | { status: 'loaded'; items: DocumentItemViewModel[] }
  | { status: 'error' };

export type OrgDocumentsPickerView =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'empty' }
  | { status: 'noResults' }
  | { status: 'loaded'; items: DocumentItemViewModel[] };

export type GiveContextStep = 'menu' | 'orgDocuments';

export type GiveContextState = {
  isOpen: boolean;
  step: GiveContextStep;
};

export type AssistantDocumentsState = {
  giveContext: GiveContextState;
  orgDocuments: OrgDocumentsState;
  selectedDocuments: DocumentReference[];
  uploadingDocuments: UploadingDocument[];
  documentSearchTerm: string;
  webSearchEnabled: boolean;
};

export const defaultAssistantDocumentsState: AssistantDocumentsState = {
  giveContext: { isOpen: false, step: 'menu' },
  orgDocuments: { status: 'loading' },
  selectedDocuments: [],
  uploadingDocuments: [],
  documentSearchTerm: '',
  webSearchEnabled: false,
};

const filterDocumentsByName = (
  items: DocumentItemViewModel[],
  searchTerm: string
): DocumentItemViewModel[] => {
  const normalizedTerm = searchTerm.trim().toLowerCase();
  if (!normalizedTerm) {
    return items;
  }
  return items.filter(item => item.name.toLowerCase().includes(normalizedTerm));
};

const toOrgDocumentsPickerView = (
  orgDocuments: OrgDocumentsState,
  searchTerm: string
): OrgDocumentsPickerView => {
  if (orgDocuments.status !== 'loaded') {
    return orgDocuments;
  }
  const supportedItems = orgDocuments.items.filter(item => item.isSupported);
  if (supportedItems.length === 0) {
    return { status: 'empty' };
  }
  const items = filterDocumentsByName(supportedItems, searchTerm);
  if (items.length === 0) {
    return { status: 'noResults' };
  }
  return { status: 'loaded', items };
};

const toDocumentItemViewModel = (
  attachment: APIAttachment,
  resourceType: DocumentResourceType
): DocumentItemViewModel => {
  const attachmentType = toAttachmentType(attachment['content-type']);
  const name = attachment.filename;
  const type = attachment['content-type'] ?? '';
  return {
    id: attachment._id,
    name,
    type,
    resourceType,
    icon: toAttachmentIcon(attachmentType),
    thumbnailUrl: attachmentType === 'image' ? attachment.uri : null,
    isSupported: isSupportedDocument({ name, type }),
  };
};

const toUserDocumentReference = (
  attachment: APIAttachment
): DocumentReference =>
  toDocumentReference(toDocumentItemViewModel(attachment, 'user'));

const handleOpenGiveContext = (
  state: AssistantDocumentsState
): AssistantDocumentsState => ({
  ...state,
  giveContext: { isOpen: true, step: 'menu' },
  documentSearchTerm: '',
});

const handleCloseGiveContext = (
  state: AssistantDocumentsState
): AssistantDocumentsState => ({
  ...state,
  giveContext: { ...state.giveContext, isOpen: false },
  documentSearchTerm: '',
});

const handleShowOrgDocumentsStep = (
  state: AssistantDocumentsState
): AssistantDocumentsState => ({
  ...state,
  giveContext: { ...state.giveContext, step: 'orgDocuments' },
});

const handleOrgDocumentsLoaded = (
  state: AssistantDocumentsState,
  attachments: APIAttachment[]
): AssistantDocumentsState => ({
  ...state,
  orgDocuments: {
    status: 'loaded',
    items: attachments.map(attachment =>
      toDocumentItemViewModel(attachment, 'org')
    ),
  },
});

const handleSelectDocument = (
  state: AssistantDocumentsState,
  document: DocumentReference
): AssistantDocumentsState => {
  const isAlreadySelected = state.selectedDocuments.some(
    selected => selected.id === document.id
  );
  if (
    isAlreadySelected ||
    state.selectedDocuments.length >= MAX_DOCUMENTS_LIMIT
  ) {
    return state;
  }
  return {
    ...state,
    selectedDocuments: [...state.selectedDocuments, document],
  };
};

const handleDeselectDocument = (
  state: AssistantDocumentsState,
  documentId: ArdoqId
): AssistantDocumentsState => ({
  ...state,
  selectedDocuments: state.selectedDocuments.filter(
    selected => selected.id !== documentId
  ),
});

const handleRemoveSelectedDocument = (
  state: AssistantDocumentsState,
  { id }: DocumentReference
): AssistantDocumentsState => ({
  ...state,
  selectedDocuments: state.selectedDocuments.filter(
    selected => selected.id !== id
  ),
});

const handleClearSelectedDocuments = (
  state: AssistantDocumentsState
): AssistantDocumentsState => ({
  ...state,
  selectedDocuments: [],
  uploadingDocuments: [],
});

const handleWebSearchFromSession = (
  state: AssistantDocumentsState,
  { webSearchEnabled }: LoadedChat
): AssistantDocumentsState => ({
  ...state,
  webSearchEnabled,
});

const handleDocumentUploadStarted = (
  state: AssistantDocumentsState,
  uploadingDocument: UploadingDocument
): AssistantDocumentsState => ({
  ...state,
  uploadingDocuments: [...state.uploadingDocuments, uploadingDocument],
});

const handleDocumentUploadSucceeded = (
  state: AssistantDocumentsState,
  { uploadId, document }: { uploadId: string; document: DocumentReference }
): AssistantDocumentsState => {
  const isStillUploading = state.uploadingDocuments.some(
    uploading => uploading.uploadId === uploadId
  );
  if (!isStillUploading) {
    return state;
  }
  const uploadingDocuments = state.uploadingDocuments.filter(
    uploading => uploading.uploadId !== uploadId
  );
  if (state.selectedDocuments.length >= MAX_DOCUMENTS_LIMIT) {
    return { ...state, uploadingDocuments };
  }
  return {
    ...state,
    uploadingDocuments,
    selectedDocuments: [...state.selectedDocuments, document],
  };
};

const handleDocumentUploadFailed = (
  state: AssistantDocumentsState,
  { uploadId }: { uploadId: string }
): AssistantDocumentsState => {
  const isStillUploading = state.uploadingDocuments.some(
    uploading => uploading.uploadId === uploadId
  );
  if (!isStillUploading) {
    return state;
  }
  return {
    ...state,
    uploadingDocuments: state.uploadingDocuments.filter(
      uploading => uploading.uploadId !== uploadId
    ),
  };
};

export const assistantDocumentsOperations = {
  toDocumentItemViewModel,
  toUserDocumentReference,
  toOrgDocumentsPickerView,
  filterDocumentsByName,
  handleOpenGiveContext,
  handleCloseGiveContext,
  handleShowOrgDocumentsStep,
  handleOrgDocumentsLoaded,
  handleSelectDocument,
  handleDeselectDocument,
  handleRemoveSelectedDocument,
  handleClearSelectedDocuments,
  handleWebSearchFromSession,
  handleDocumentUploadStarted,
  handleDocumentUploadSucceeded,
  handleDocumentUploadFailed,
};
